import { createSelector } from '@reduxjs/toolkit';
import { RootState } from './index';
import { Permissions } from '@/types/permissions';
import { ADMIN_ROLE } from '@/constants/constants';

// Auth selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsLoading = (state: RootState) => state.auth.isLoading;
export const selectUser = (state: RootState) => state.auth.user;
export const selectUserRole = (state: RootState) => state.auth.role;
export const selectUserPermissions = (state: RootState) => state.auth.permissions;
export const selectUserPermissionObjects = (state: RootState) => state.auth.permissionObjects;
export const selectIssuperUser = (state: RootState) => state.auth.user?.is_superuser || false;
export const selectToken = (state: RootState) => state.auth.token;

// Memoized selectors
export const selectHasPermission = createSelector(
  [selectUserPermissions, selectUserRole, selectIssuperUser, (_: RootState, permission: Permissions) => permission],
  (permissions, userRole, issuperUser, permission) => 
    permissions.includes(permission) || userRole === ADMIN_ROLE || issuperUser
);

export const selectHasAnyPermission = createSelector(
  [selectUserPermissions, selectUserRole, selectIssuperUser, (_: RootState, requiredPermissions: Permissions[]) => requiredPermissions],
  (permissions, userRole, issuperUser, requiredPermissions: Permissions[]) => {
    if (userRole === ADMIN_ROLE || issuperUser) return true;
    return requiredPermissions.some(permission => permissions.includes(permission));
  }
);

export const selectHasAllPermissions = createSelector(
  [selectUserPermissions, selectUserRole, selectIssuperUser, (_: RootState, requiredPermissions: Permissions[]) => requiredPermissions],
  (permissions, userRole, issuperUser, requiredPermissions: Permissions[]) => {
    if (userRole === ADMIN_ROLE || issuperUser) return true;
    return requiredPermissions.every(permission => permissions.includes(permission));
  }
);

// Check if user is system admin
export const selectIsSystemAdmin = createSelector(
  [selectUserRole, selectIssuperUser],
  (userRole, issuperUser) => userRole === ADMIN_ROLE || issuperUser
);

// Combined auth and permission selectors
export const selectUserInfo = createSelector(
  [selectUser, selectUserRole, selectUserPermissions, selectUserPermissionObjects, selectIssuperUser],
  (user, role, permissions, permissionObjects, issuperUser) => ({
    user,
    role,
    permissions,
    permissionObjects,
    issuperUser
  })
);

export const selectIsAuthReady = createSelector(
  [selectIsLoading],
  (authLoading) => !authLoading
); 

// LienSummaryConfig selectors
export const selectDocuments = (state: RootState) => state.lienSummaryConfig.documents;
export const selectSelectedDocument = (state: RootState) => state.lienSummaryConfig.selectedDocument;
export const selectDocumentsLoading = (state: RootState) => state.lienSummaryConfig.loading;
export const selectDocumentsError = (state: RootState) => state.lienSummaryConfig.error;

// Helper selectors for documents
export const selectDocumentById = (state: RootState, id: number) => 
  state.lienSummaryConfig.documents.find(doc => doc.id === id);

export const selectActiveDocuments = (state: RootState) => 
  state.lienSummaryConfig.documents.filter(doc => 
    doc.status?.toUpperCase() === 'ACTIVE' || doc.status === 'Active'
  );

export const selectInactiveDocuments = (state: RootState) => 
  state.lienSummaryConfig.documents.filter(doc => 
    doc.status?.toUpperCase() === 'INACTIVE' || doc.status === 'Inactive'
  );

// System selectors
export const selectTimezones = (state: RootState) => state.system.timezones;
export const selectIsLoadingTimezones = (state: RootState) => state.system.isLoadingTimezones;
export const selectTimezonesFetched = (state: RootState) => state.system.fetched;
export const selectSystemError = (state: RootState) => state.system.error;